import React, { FC, useEffect, useRef, useState } from 'react'
import { I18n } from 'aws-amplify'
import classnames from 'classnames'
import { Route, Switch, useRouteMatch, useHistory } from 'react-router-dom'
import { makeStyles, Theme, AppBar, Toolbar, IconButton, Grid, Snackbar, Typography } from '@material-ui/core'
import { Menu as MenuIcon } from '@material-ui/icons'
import { Alert } from '@material-ui/lab'

import {
  ProfileMenu,
  MobileWelcome,
  MobileDemos,
  MobileContact,
  PageNotFound,
  IntroTutorial,
  MobileDemo,
  About,
  QRCode,
  Notice,
  MobileEventReplay
} from 'components'

import { ReactComponent as VerizonLogo } from 'assets/verizon-logo.svg'
import { ReactComponent as LiveIcon } from 'assets/live-icon.svg'
import { useAppState } from 'providers'

import { useBrowserCache } from '../hooks'
import { JoyrideTutorialStyles } from 'components/shared/tutorial/JoyrideTutorialStyles'
import { createInteraction, Demos, introTutorialSteps, Sessions } from 'helpers'
import { EventStages, ISubscriptionObject, IUser, Routes } from 'types'
import { graphQLSubscription } from '../graphql/helpers'
import { onCreateNotification } from '../graphql/subscriptions'

interface MobileWrapperProps {
  user: IUser | null
  eventStage?: EventStages
}

export const MobileWrapper: FC<MobileWrapperProps> = ({ user, eventStage }) => {
  const classes = useStyles()
  const history = useHistory()
  const {
    appState: { showLivestream }
  } = useAppState()
  let noticeSubscription = useRef<ISubscriptionObject | null>(null)

  const { path } = useRouteMatch()
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [infoMessage, setInfoMessage] = useState<string | null>(null)
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [showSurvey, setShowSurvey] = useState<boolean>(false)
  const [showLivestreamNotice, setShowLivestreamNotice] = useState<boolean>(false)
  const [breakoutNoticeOpen, setBreakoutNoticeOpen] = useState<boolean>(false)
  const [eventReplayNoticeOpen, setEventReplayNoticeOpen] = useState<boolean>(false)

  const [welcomeTutorialViewedLoading, welcomeTutorialViewed, setWelcomeTutorialViewed] = useBrowserCache(
    'welcome-tutorial-dismissed'
  )

  const newNotificationCreated = ({ onCreateNotification }) => {
    if (onCreateNotification.type === 'survey') {
      setShowSurvey(true)
    }
  }

  useEffect(() => {
    if (!welcomeTutorialViewed) {
      setShowTooltip(true)
    }

    noticeSubscription.current = graphQLSubscription(onCreateNotification, {}, newNotificationCreated)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (eventStage === EventStages.LIVESTREAM || eventStage === EventStages.COUNTDOWN) {
      setShowLivestreamNotice(true)
    } else if (eventStage && [EventStages.LIVESTREAMENDING, EventStages.POSTLIVESTREAM].includes(eventStage)) {
      setBreakoutNoticeOpen(true)
    } else if (eventStage === EventStages.POSTEVENT) {
      setEventReplayNoticeOpen(true)
    }
    // eslint-disable-next-line
  }, [eventStage])

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen)
  }

  const toggleTutorial = () => {}

  return (
    <div className={classes.root}>
      <AppBar position='sticky' className={classes.header}>
        <Toolbar>
          <Grid container alignItems='center' justify='space-between'>
            <Grid item>
              <VerizonLogo width={90} />
            </Grid>
            <Grid item className={classes.rightNav}>
              <Typography
                component='span'
                variant='h5'
                className={classnames(
                  classes.livestreamItem,
                  !showLivestream && eventStage !== EventStages.POSTEVENT && classes.livestreamDisabled
                )}
                onClick={() => {
                  if (eventStage === EventStages.POSTEVENT) {
                    history.push(`/event/event-replay/fullShow`)
                  } else if (showLivestream) {
                    createInteraction('livestream', user?.email || '', 'navigation', 'clicked', eventStage)
                    history.push(`/${Routes.stream}`)
                  }
                }}
              >
                {eventStage !== EventStages.POSTEVENT ? (
                  <span className={classes.liveIcon}>
                    <LiveIcon width={30} height={20} className={classnames(showLivestream && 'pulse')} />
                    {I18n.get('liveStream')}
                  </span>
                ) : (
                  <span>Event replay</span>
                )}
              </Typography>
              <IconButton onClick={() => toggleDrawer()} id='mobileMenu'>
                <MenuIcon className={classes.menuIcon} />
              </IconButton>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      <div className={classes.content}>
        <Switch>
          <Route path={path} exact>
            <MobileWelcome eventStage={eventStage} />
          </Route>

          {/* EVENT REPLAY */}
          <Route path={`${path}/event-replay/:eventReplayId`} exact>
            <MobileEventReplay />
          </Route>

          {/* DEMOS */}
          <Route path={`${path}/other-use-cases`} exact>
            <MobileDemos
              title='Experience the power of Verizon 5G Ultra Wideband.'
              bodyText='What can Verizon 5G Ultra Wideband do for your business? Explore the various ways our 5G network is moving businesses like yours forward.'
              demos={Demos}
            />
          </Route>

          <Route path={`${path}/${Routes.demos}/:demoId`} exact>
            <MobileDemo />
          </Route>

          {/* SESSIONS */}
          <Route path={`${path}/${Routes.sessions}`} exact>
            <MobileDemos
              title='See what 5G can do for the DOD.'
              bodyText='Explore how we’re bringing together 5G technology and our defense agency partners to develop new capabilities to answer DOD challenges.'
              demos={Sessions}
            />
          </Route>

          <Route path={`${path}/${Routes.sessions}/:demoId`} exact>
            <MobileDemo isSession={true} />
          </Route>

          {/* CONTACT US */}
          <Route path={`${path}/${Routes.contact}/:demo?`} exact>
            <MobileContact setErrorMessage={setErrorMessage} setSuccessMessage={setSuccessMessage} isSupport={false} />
          </Route>

          {/* SUPPORT */}
          <Route path={`${path}/${Routes.support}`} exact>
            <MobileContact setErrorMessage={setErrorMessage} setSuccessMessage={setSuccessMessage} isSupport={true} />
          </Route>

          {/* ABOUT */}
          <Route path={`${path}/${Routes.about}`} exact>
            <div className={classes.pageWrapper}>
              <About />
            </div>
          </Route>

          {/* This isn't a duplicate - Every Switch needs a nomatch at the end for the 404 page to work*/}
          <Route path={`${path}/*`} component={PageNotFound} />
        </Switch>
      </div>

      <QRCode showModal={showSurvey && welcomeTutorialViewed} setShowModal={setShowSurvey} />

      {/* Post-Livestream Breakout Notice */}
      {breakoutNoticeOpen && welcomeTutorialViewed ? (
        <Notice
          message={'Watch Verizon partners discuss 5G and its implications on their DOD specific solutions.'}
          onClick={() => {
            history.push(`${path}/${Routes.sessions}`)
            setBreakoutNoticeOpen(false)
          }}
          onClose={() => setBreakoutNoticeOpen(false)}
          buttonText='DOD 5G use cases'
        />
      ) : null}

      {/* Post-Event Notice */}
      {eventReplayNoticeOpen && welcomeTutorialViewed ? (
        <Notice
          message={
            'See why 5G matters to the DOD, how it can help securely shape missions, and hear defense-focused use cases.'
          }
          onClick={() => {
            history.push(`${path}/event-replay/fullShow`)
            setEventReplayNoticeOpen(false)
          }}
          onClose={() => setEventReplayNoticeOpen(false)}
          buttonText='Watch event replay'
        />
      ) : null}

      {showLivestreamNotice &&
      welcomeTutorialViewed &&
      (eventStage === EventStages.COUNTDOWN || eventStage === EventStages.LIVESTREAM) ? (
        <Notice
          message={
            eventStage === EventStages.COUNTDOWN
              ? 'The live stream is about to begin.'
              : 'The live stream is now in session.'
          }
          streamUrl='/stream'
          onClose={() => setShowLivestreamNotice(false)}
        />
      ) : null}

      <ProfileMenu
        drawerOpen={drawerOpen}
        user={user}
        toggleDrawer={toggleDrawer}
        toggleTutorial={toggleTutorial}
        toggleIntroTutorial={() => {
          setWelcomeTutorialViewed(false)
          toggleDrawer()
        }}
        eventStage={eventStage}
      />

      {(Boolean(successMessage) || Boolean(errorMessage) || Boolean(infoMessage)) && (
        <Snackbar
          open={Boolean(successMessage) || Boolean(errorMessage) || Boolean(infoMessage)}
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={() => {
            successMessage && setSuccessMessage(null)
            errorMessage && setErrorMessage(null)
            infoMessage && setInfoMessage(null)
          }}
        >
          <Alert severity={successMessage ? 'success' : infoMessage ? 'info' : 'error'} variant='filled'>
            {successMessage ? successMessage : infoMessage ? infoMessage : errorMessage}
          </Alert>
        </Snackbar>
      )}
      {showTooltip && !welcomeTutorialViewedLoading && (
        <IntroTutorial
          run={!welcomeTutorialViewed}
          steps={introTutorialSteps(user, true)}
          onClose={() => {
            setWelcomeTutorialViewed(true)
          }}
          styles={JoyrideTutorialStyles}
        />
      )}
    </div>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    height: '100%'
  },
  header: {
    backgroundColor: '#fff',
    color: '#000',
    height: 52
  },
  menuIcon: {
    color: '#000'
  },
  content: {
    height: 'calc(100% - 52px)',
    overflow: 'auto',
    color: '#000'
  },
  rightNav: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  livestreamItem: {
    color: '#000',
    display: 'none',

    [theme.breakpoints.down('xs')]: {
      display: 'block'
    }
  },
  liveIcon: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: '.5rem'
    }
  },
  livestreamDisabled: {
    color: '#979797',
    '& svg': {
      '& .icon-bg': {
        fill: '#979797 !important'
      }
    },
    cursor: 'not-allowed'
  },
  pageWrapper: {
    padding: '2rem'
  }
}))
